import React, { useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { useCatalog } from "@/state/CatalogContext";
import { useConfig } from "@/lib/useConfig";
import { inCategory } from "@/lib/categories";
import { fetchProduct, fetchReviews, mediaUrl } from "@/lib/api";
import { formatUzs } from "@/lib/format";
import { haptic } from "@/lib/telegram";
import { InquirySheet } from "@/components/InquirySheet";
import { ReviewCard } from "@/components/ReviewCard";
import { NoteIcon } from "@/components/Flavor";
import { RichText } from "@/components/RichText";
import { DI, DProductCard, PATH, WI, dsvg, flyToCart, reviewsText } from "@/components/desktop/desktopUi";
import { useImageLook } from "@/lib/useImageFit";
import type { Product, Review } from "@/types";

type Tab = "desc" | "specs" | "flavor" | "reviews" | "delivery";
const findAttr = (p: Product, re: RegExp) => {
  const a = p.attributes ?? {};
  const k = Object.keys(a).find((x) => re.test(x));
  return k ? { key: k, value: a[k] } : null;
};
const CHIP_RE = { process: /обработ|process|ishlov/i, region: /регион|происхожд|страна|region|origin|mintaqa/i, altitude: /высот|altitude|baland/i };
const Stars = ({ value }: { value: number }) => <span className="dp-stars">{"★★★★★".slice(0, Math.round(value))}<i>{"★★★★★".slice(Math.round(value))}</i></span>;

/** Thumbnail: packshots shown whole on their own background colour, lifestyle photos fill the frame. */
function Thumb({ src, active, onClick, label }: { src: string; active: boolean; onClick: () => void; label: string }) {
  const url = mediaUrl(src, 200) ?? "";
  const look = useImageLook(url);
  return (
    <button data-active={active} onClick={onClick} aria-label={label} style={look.bg ? { background: look.bg } : undefined}>
      {src && <img src={url} alt="" data-fit={look.fit} />}
    </button>
  );
}

/**
 * Main photo. The frame takes the photo's own background colour, so a packshot
 * doesn't look like a picture lying on a background. Hovering shows a magnifier
 * with the full-size original, so the small print on a pack or a machine can be read.
 */
function MainPhoto({ src, alt, children }: { src: string; alt: string; children?: React.ReactNode }) {
  const url = mediaUrl(src, 800) ?? "";
  const full = mediaUrl(src) ?? url;
  const look = useImageLook(url);
  const box = useRef<HTMLDivElement>(null);
  const pic = useRef<HTMLImageElement>(null);
  const [lens, setLens] = useState<{ x: number; y: number; w: number; h: number; px: number; py: number } | null>(null);
  const ZOOM = 2.6;
  const SIZE = 210;
  const move = (e: React.MouseEvent) => {
    const im = pic.current;
    const bx = box.current;
    if (!im || !bx || !im.naturalWidth || (e.target as HTMLElement).closest("button")) return setLens(null);
    const r = im.getBoundingClientRect();
    const k = look.fit === "contain" ? Math.min(r.width / im.naturalWidth, r.height / im.naturalHeight) : Math.max(r.width / im.naturalWidth, r.height / im.naturalHeight);
    const w = im.naturalWidth * k;
    const h = im.naturalHeight * k;
    const x = e.clientX - (r.left + (r.width - w) / 2);
    const y = e.clientY - (r.top + (r.height - h) / 2);
    if (x < 0 || y < 0 || x > w || y > h) return setLens(null);
    const b = bx.getBoundingClientRect();
    setLens({ x: e.clientX - b.left, y: e.clientY - b.top, w, h, px: x, py: y });
  };
  return (
    <div className="dp-main" ref={box} data-fit={look.fit} style={look.bg ? { background: look.bg } : undefined} onMouseMove={move} onMouseLeave={() => setLens(null)}>
      {src && <img ref={pic} src={url} alt={alt} data-fit={look.fit} />}
      {lens && (
        <span
          className="dp-lens"
          aria-hidden="true"
          style={{
            width: SIZE,
            height: SIZE,
            left: lens.x - SIZE / 2,
            top: lens.y - SIZE / 2,
            backgroundColor: look.bg ?? "#fff",
            backgroundImage: `url("${full}"), url("${url}")`,
            backgroundSize: `${lens.w * ZOOM}px ${lens.h * ZOOM}px`,
            backgroundPosition: `${SIZE / 2 - lens.px * ZOOM}px ${SIZE / 2 - lens.py * ZOOM}px`,
          }}
        />
      )}
      {children}
    </div>
  );
}

/** Desktop product page — coffee (cart) and equipment (request) layouts from the approved references. */
export function DesktopProduct() {
  const { t, locale } = useI18n();
  const { route, navigate } = useRouter();
  const { getProduct, products } = useCatalog();
  const { addItem, isFavorite, toggleFavorite } = useCart();
  const { botUsername, delivery } = useConfig();
  const slug = route.name === "product" ? route.slug : "";
  const cached = useMemo(() => products.find((p) => p.slug === slug), [products, slug]);
  const [product, setProduct] = useState<Product | null>(cached ?? null);
  const [missing, setMissing] = useState(false);
  const [img, setImg] = useState(0);
  const [variantId, setVariantId] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState<Tab>("desc");
  const [reviews, setReviews] = useState<{ items: Review[]; total: number } | null>(null);
  const [inquiry, setInquiry] = useState(false);
  const [added, setAdded] = useState(false);

  // New product → reset the gallery, pack, quantity and tab.
  useEffect(() => {
    setImg(0); setQty(1); setTab("desc"); setVariantId(null); setMissing(false);
  }, [slug]);
  // Product texts are localised on load, so reload them when the language changes too.
  useEffect(() => {
    let stale = false; // a newer product or language replaced this request
    if (cached) setProduct(cached);
    fetchProduct(slug)
      .then((p) => { if (!stale) (p ? setProduct(p) : !cached && setMissing(true)); })
      .catch(() => { if (!stale && !cached) setMissing(true); });
    fetchReviews(slug, 6)
      .then((r) => { if (!stale) setReviews(r); })
      .catch(() => { if (!stale) setReviews({ items: [], total: 0 }); });
    return () => { stale = true; };
  }, [slug, locale]);

  if (missing) return <div className="dk-wrap dp-missing"><h1 className="dk-serif">{t("desktop.productMissing")}</h1><button className="dk-btn" onClick={() => navigate({ name: "catalog" })}>{t("desktop.allCatalog")} {DI.arrow}</button></div>;
  if (!product)
    return (
      <div className="dk-wrap dp-sk" aria-busy="true" aria-label={t("common.loading")}>
        <div className="dk-sk dp-sk__img" />
        <div className="dp-sk__info">
          <div className="dk-sk" style={{ height: 12, width: "30%" }} />
          <div className="dk-sk" style={{ height: 40, width: "75%", marginTop: 14 }} />
          <div className="dk-sk" style={{ height: 14, width: "90%", marginTop: 22 }} />
          <div className="dk-sk" style={{ height: 14, width: "80%", marginTop: 10 }} />
          <div className="dk-sk" style={{ height: 28, width: "35%", marginTop: 28 }} />
          <div className="dk-sk" style={{ height: 50, width: "60%", marginTop: 22, borderRadius: 999 }} />
        </div>
      </div>
    );

  const p = product;
  const coffee = p.category === "coffee";
  const underOrder = p.orderMode === "under_order";
  const variant = p.variants.find((v) => v.id === variantId) ?? p.variants[0];
  const images = p.images.length ? p.images : [""];
  const fav = isFavorite(p.id);
  const roast = p.flavor?.roastLevel ?? null;
  const chips = [
    roast ? { icon: PATH.bean, label: t("desktop.chipRoast"), value: t(`flavor.roast${roast}`) } : null,
    findAttr(p, CHIP_RE.process) && { icon: PATH.leaf, label: t("desktop.chipProcess"), value: findAttr(p, CHIP_RE.process)!.value },
    findAttr(p, CHIP_RE.region) && { icon: '<path d="M3 19 9 9l4 6 3-4 5 8z"/>', label: t("desktop.chipRegion"), value: findAttr(p, CHIP_RE.region)!.value },
    findAttr(p, CHIP_RE.altitude) && { icon: '<path d="M3 19 10 6l4 7 2-3 5 9z"/>', label: t("desktop.chipAltitude"), value: findAttr(p, CHIP_RE.altitude)!.value },
  ].filter(Boolean) as { icon: string; label: string; value: string }[];
  const chipKeys = new Set(Object.values(CHIP_RE).map((re) => findAttr(p, re)?.key).filter(Boolean));
  const features = Object.entries(p.attributes ?? {}).filter(([k]) => !chipKeys.has(k)).slice(0, 6);
  const sameGroup = products.filter((x) => x.id !== p.id && (p.categoryRef ? inCategory(x, p.categoryRef.slug) : x.category === p.category)).slice(0, 6);
  const coffeeForEquipment = products.filter((x) => x.category === "coffee").slice(0, 4);
  const accessories = products.filter((x) => x.id !== p.id && x.category === "accessories").slice(0, 4);
  const crumbCat = p.categoryRef;

  const add = () => {
    if (!variant) return;
    haptic("light");
    addItem(p.id, variant.id, qty);
    flyToCart(document.querySelector(".dp-add"));
    window.setTimeout(() => window.dispatchEvent(new Event("kms:cart-open")), 650);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1800);
  };
  const tabs: [Tab, string][] = [
    ["desc", t("desktop.tabDesc")],
    ["specs", t("desktop.tabSpecs")],
    ...(coffee && p.flavor ? ([["flavor", t("desktop.tabFlavor")]] as [Tab, string][]) : []),
    ["reviews", `${t("desktop.tabReviews")}${reviews?.total ? ` (${reviews.total})` : ""}`],
    ["delivery", t("desktop.tabDelivery")],
  ];

  const gallery = (
    <div className="dp-gallery">
      <div className="dp-thumbs">
        {images.slice(0, 6).map((src, i) => (
          <Thumb key={i} src={src} active={i === img} onClick={() => setImg(i)} label={`${i + 1}`} />
        ))}
      </div>
      <MainPhoto src={images[img] ?? ""} alt={p.name}>
        <button className="dp-heart" data-on={fav} onClick={() => toggleFavorite(p.id)} aria-label={t("profile.favorites")}>{DI.heart}</button>
      </MainPhoto>
    </div>
  );

  const services = (
    <div className="dp-services">
      <div>{WI(PATH.truck, 28)}<span><b>{t("desktop.svcDelivery")}</b>{t("desktop.svcDeliveryS")}</span></div>
      <div>{WI('<path d="M12 3 4 7v10l8 4 8-4V7z"/>', 28)}<span><b>{t("desktop.svcPack")}</b>{t("desktop.svcPackS")}</span></div>
      <div>{WI(PATH.leaf, 28)}<span><b>{t("desktop.svcFresh")}</b>{t("desktop.svcFreshS")}</span></div>
      <div>{WI('<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/>', 28)}<span><b>{t("desktop.svcPickup")}</b>{t("desktop.svcPickupS")}</span></div>
    </div>
  );

  return (
    <div className="dp">
      <span className="dp-art" aria-hidden="true" />
      <div className={`dp-wrap ${!coffee ? "dp-wrap--wide" : ""}`}>
        <div className="dc-crumbs dp-crumbs">
          <button onClick={() => navigate({ name: "home" })}>{t("desktop.crumbHome")}</button> › <button onClick={() => navigate({ name: "catalog" })}>{t("desktop.catalog")}</button>
          {crumbCat && <> › <button onClick={() => navigate({ name: "catalog", category: crumbCat.slug })}>{crumbCat.name}</button></>} › <span>{p.name}</span>
        </div>

        {coffee ? (
          /* ------------------------- coffee / buy now ------------------------- */
          <div className="dp-top dp-top--coffee">
            {gallery}
            <div className="dp-info">
              <div className="dk-eyebrow">{crumbCat?.name ?? t(`catalog.categories.${p.category}`)}</div>
              <h1 className="dk-serif">{p.name}</h1>
              {p.shortDescription && <div className="dp-lead">{p.shortDescription}</div>}
              {(p.rating ?? 0) > 0 && (
                <div className="dp-rating"><Stars value={p.rating ?? 0} /><b>{(p.rating ?? 0).toFixed(1)}</b><button onClick={() => setTab("reviews")}>({reviewsText(p.reviewsCount ?? reviews?.total ?? 0, locale, t)})</button></div>
              )}
              <div className="dp-price dk-serif">{variant ? formatUzs(variant.priceUzs, locale) : ""}{variant?.oldPriceUzs ? <s>{formatUzs(variant.oldPriceUzs, locale)}</s> : null}</div>
              {p.variants.length > 1 && (
                <>
                  <div className="dp-label">{t("desktop.weight")}</div>
                  <div className="dp-variants">
                    {p.variants.map((v) => (
                      <button key={v.id} data-active={v.id === variant?.id} disabled={v.stock <= 0} onClick={() => setVariantId(v.id)}>
                        {v.label}<br /><b>{formatUzs(v.priceUzs, locale)}</b>
                      </button>
                    ))}
                  </div>
                </>
              )}
              {chips.length > 0 && (
                <div className="dp-chips" style={{ gridTemplateColumns: `repeat(${Math.max(chips.length, 4)}, 1fr)` }}>
                  {chips.map((c) => <div key={c.label}>{WI(c.icon, 28)}<span>{c.label}<b>{c.value}</b></span></div>)}
                </div>
              )}
              {underOrder ? (
                <>
                  <div className="dp-box__status dp-status-inline"><i />{t("desktop.underOrderNote")}</div>
                  <div className="dp-buy">
                    <button className="dk-btn dp-add" onClick={() => setInquiry(true)}>{t("desktop.leaveRequest")}</button>
                    {botUsername && <a className="dk-btn dk-btn--ghost dp-fav" href={`https://t.me/${botUsername}`} target="_blank" rel="noreferrer">{t("desktop.writeTelegram")}</a>}
                    <button className="dk-btn dk-btn--ghost dp-fav dp-fav--icon" data-on={fav} onClick={() => toggleFavorite(p.id)} aria-label={t("profile.favorites")}>{DI.heart}</button>
                  </div>
                </>
              ) : (
              <div className="dp-buy">
                <span className="dp-qty">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="−">−</button>
                  <b>{qty}</b>
                  <button onClick={() => setQty((q) => Math.min(99, variant ? Math.max(1, Math.min(99, variant.stock)) : 99, q + 1))} aria-label="+">+</button>
                </span>
                <button className="dk-btn dp-add" disabled={!variant || variant.stock <= 0} onClick={add}>
                  {DI.cart}{variant && variant.stock <= 0 ? t("product.outOfStock") : added ? t("desktop.added") : t("desktop.addToCart")}
                </button>
                <button className="dk-btn dk-btn--ghost dp-fav" data-on={fav} onClick={() => toggleFavorite(p.id)}>{DI.heart}{fav ? t("desktop.inFavorites") : t("desktop.toFavorites")}</button>
              </div>
              )}
              {added && <button className="dp-gocart" onClick={() => navigate({ name: "cart" })}>{t("desktop.goToCart")} {DI.arrow}</button>}
              {services}
            </div>
          </div>
        ) : (
          /* ------------------------- equipment / request ------------------------- */
          <div className="dp-top dp-top--eq">
            {gallery}
            <div className="dp-info">
              <div className="dk-eyebrow">{p.brand || crumbCat?.name || t(`catalog.categories.${p.category}`)}</div>
              <h1 className="dk-serif">{p.name}</h1>
              {p.shortDescription && <div className="dp-lead">{p.shortDescription}</div>}
              {(p.rating ?? 0) > 0 && <div className="dp-rating"><Stars value={p.rating ?? 0} /><b>{(p.rating ?? 0).toFixed(1)}</b><span>({reviewsText(p.reviewsCount ?? 0, locale, t)})</span></div>}
              {p.description && <div className="dp-excerpt">{p.description.replace(/[#*]/g, "").slice(0, 320)}{p.description.length > 320 ? "…" : ""}</div>}
              {features.length > 0 && (
                <div className="dp-eqfeat">
                  {features.slice(0, 4).map(([k, v]) => <div key={k}><b>{v}</b><span>{k}</span></div>)}
                </div>
              )}
            </div>
            <aside className="dp-box">
              <div className="dp-box__price dk-serif">{variant && variant.priceUzs > 0 ? formatUzs(variant.priceUzs, locale) : t("catalog.onRequest")}</div>
              <div className="dp-box__status"><i />{t("desktop.underOrderNote")}</div>
              <button className="dk-btn dp-box__btn" onClick={() => setInquiry(true)}>{t("desktop.leaveRequest")}</button>
              {botUsername && <a className="dk-btn dk-btn--ghost dp-box__btn" href={`https://t.me/${botUsername}`} target="_blank" rel="noreferrer">{t("desktop.writeTelegram")}</a>}
              <button className="dp-box__row" onClick={() => setInquiry(true)}>{WI('<path d="M4 6h16v12H4zM8 10h8M8 14h5"/>', 30)}<span><b>{t("desktop.pickKit")}</b>{t("desktop.pickKitS")}</span>{DI.chevR}</button>
              <button className="dp-box__row" onClick={() => setInquiry(true)}>{WI('<path d="M5 15v-3a7 7 0 0 1 14 0v3M5 15a2 2 0 0 0 2 2h1v-5H7a2 2 0 0 0-2 2zM19 15a2 2 0 0 1-2 2h-1v-5h1a2 2 0 0 1 2 2z"/>', 30)}<span><b>{t("desktop.consult")}</b>{t("desktop.consultS")}</span>{DI.chevR}</button>
              <div className="dp-box__svc"><span>{WI(PATH.truck, 22)}{t("desktop.svcDelivery")}</span><span>{WI('<path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z"/>', 22)}{t("desktop.warranty")}</span>{p.category === "machines" || p.category === "grinders" ? <span>{WI(PATH.gear, 22)}{t("desktop.setup")}</span> : <span>{WI(PATH.check, 22)}{t("desktop.advice")}</span>}</div>
            </aside>
          </div>
        )}

        {/* ------------------------------ tabs ------------------------------ */}
        <div className="dp-tabs">{tabs.map(([k, label]) => <button key={k} data-active={tab === k} onClick={() => setTab(k)}>{label}</button>)}</div>

        {tab === "desc" && (
          <div className={`dp-desc ${coffee && p.flavor?.notes?.length ? "dp-desc--3" : ""}`}>
            <div className="dp-desc__text">
              <h2 className="dk-serif">{coffee ? t("desktop.aboutCoffee") : t("desktop.aboutItem")}</h2>
              {p.description ? <RichText text={p.description} /> : <p>{p.shortDescription}</p>}
            </div>
            {coffee && (p.flavor?.notes?.length ?? 0) > 0 && (
              <div className="dp-notes">
                <h3 className="dk-serif">{t("desktop.inTaste")}</h3>
                <div>{p.flavor!.notes!.slice(0, 5).map((n) => <span key={n}><i><NoteIcon note={n} /></i>{t(`flavor.notes.${n}`)}</span>)}</div>
              </div>
            )}
            {features.length > 0 && (
              <div className="dp-keys">
                <h3 className="dk-serif">{t("desktop.keyFeatures")}</h3>
                {features.map(([k, v]) => <div key={k}>{dsvg('<circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/>', 20, 1.3)}<span><b>{k}:</b> {v}</span></div>)}
              </div>
            )}
          </div>
        )}
        {tab === "specs" && (
          <div className="dp-specs">
            {Object.entries(p.attributes ?? {}).length === 0 ? <p>{t("desktop.noSpecs")}</p> : Object.entries(p.attributes ?? {}).map(([k, v]) => <div key={k}><span>{k}</span><b>{v}</b></div>)}
            {p.variants.map((v) => <div key={v.id}><span>{t("desktop.pack")}</span><b>{v.label} — {formatUzs(v.priceUzs, locale)}</b></div>)}
          </div>
        )}
        {tab === "flavor" && p.flavor && (
          <div className="dp-flavor">
            {(["acidity", "sweetness", "body", "bitterness"] as const).map((k) => (
              <div key={k}><span>{t(`flavor.${k}`)}</span><div className="dp-bar">{[1, 2, 3, 4, 5].map((i) => <i key={i} data-on={i <= (p.flavor?.[k] ?? 0)} />)}</div></div>
            ))}
          </div>
        )}
        {tab === "reviews" && (
          <div className="dp-reviews">
            {reviews && reviews.items.length > 0 ? reviews.items.map((r) => <ReviewCard key={r.id} review={r} />) : <p className="dp-empty">{t("desktop.noReviews")}</p>}
          </div>
        )}
        {tab === "delivery" && (
          <div className="dp-delivery">
            <div><h3 className="dk-serif">{t("desktop.dlvTitle")}</h3><p>{t("desktop.dlvText", { fee: formatUzs(delivery.yandexFeeUzs, locale), free: formatUzs(delivery.freeThresholdUzs, locale) })}</p></div>
            <div><h3 className="dk-serif">{t("desktop.pickupTitle")}</h3><p>{t("desktop.pickupText")}</p></div>
            <div><h3 className="dk-serif">{t("desktop.payTitle")}</h3><p>{t("desktop.payText")}</p></div>
          </div>
        )}

        {/* --------------------------- related products --------------------------- */}
        {coffee ? (
          sameGroup.length > 0 && (
            <>
              <div className="dh-sec"><h2 className="dk-serif">{t("desktop.similar")}</h2><button className="dk-more" onClick={() => navigate({ name: "catalog", category: crumbCat?.slug })}>{t("desktop.allProducts")} {DI.arrow}</button></div>
              <div className="dp-related">{sameGroup.map((x) => <DProductCard key={x.id} product={x} imgHeight={150} />)}</div>
            </>
          )
        ) : (
          <div className="dp-related2">
            {accessories.length > 0 && (
              <div><div className="dh-sec"><h2 className="dk-serif">{t("desktop.compatible")}</h2></div><div className="dp-related dp-related--4">{accessories.map((x) => <DProductCard key={x.id} product={x} imgHeight={130} />)}</div></div>
            )}
            {coffeeForEquipment.length > 0 && (
              <div><div className="dh-sec"><h2 className="dk-serif">{t("desktop.boughtWith")}</h2></div><div className="dp-related dp-related--4">{coffeeForEquipment.map((x) => <DProductCard key={x.id} product={x} imgHeight={130} />)}</div></div>
            )}
          </div>
        )}
      </div>
      <InquirySheet product={p} open={inquiry} onClose={() => setInquiry(false)} mode="equipment" />
    </div>
  );
}
